// Issue #203 — the item-level control for expanded mode: "check/uncheck every
// participant's pill at once" instead of CompletionControl's "toggle my own state".
//
// T27 follow-up: a click now advances every relevant participant one tier forward
// (unstaged -> staged -> packed -> unstaged) when staging is on, the row-level
// equivalent of advanceItemStaging — it used to force straight to packed/unpacked,
// which meant this box could never leave anyone in "staged". With staging off it still
// shows the plain checked/indeterminate pair (there's no staged tier to distinguish).
//
// Follow-up: both the display and the click target are scoped to affectableParticipants
// (self + managed profiles, or everyone with allowProxyCompletion on) rather than every
// relevant participant. With proxy off, a real co-traveler's entry only ever moves via
// their own pill — an aggregate over everyone would sit stuck at "unstaged" (or
// indeterminate) forever even after the actor's own entry reaches "packed".
//
// Follow-up: when the toolbar's assignee filter narrows the list to one person,
// ExpandedParticipantPills is hidden as redundant (the row is already scoped to them),
// so this checkbox switches from "toggle everyone" to "toggle just that person" —
// otherwise a single click would silently affect participants no longer shown.

import type { ReactNode } from "react";
import { Checkbox } from "@pack-it-up/ui-web";
import { affectableParticipants, affectableRowPackingState, packingStateOf, type Completable, type CompletionField, type ParticipantContext } from "@pack-it-up/core";
import type { ParticipantId, UserId } from "@pack-it-up/shared";

export const ExpandedCompletionControl = ({
  item,
  list,
  actor,
  canEdit,
  field = "completions",
  stagingEnabled = false,
  filterParticipant,
  onAdvanceAll,
  onToggle,
  imperative = "Pack",
}: {
  item: Completable & { name: string };
  list: ParticipantContext;
  // Who can this control actually move — scopes both the display and the click target to
  // affectableParticipants (see module comment above).
  actor: UserId;
  canEdit: boolean;
  field?: CompletionField;
  stagingEnabled?: boolean;
  // Set when the toolbar is filtering the list to a single assignee — scopes the
  // checkbox to that participant instead of aggregating across everyone.
  filterParticipant?: ParticipantId;
  onAdvanceAll: () => void;
  // Required when filterParticipant is set; unused otherwise.
  onToggle?: (target: ParticipantId, targetIsUser: boolean) => void;
  imperative?: string;
}): ReactNode => {
  if (filterParticipant !== undefined) {
    const map = field === "completions" ? item.completions : item.staging;
    const packed = map?.byParticipant[filterParticipant]?.done ?? false;
    const targetIsUser = list.members[filterParticipant]?.kind === "user";
    const targetName = list.members[filterParticipant]?.profile.displayName;
    return (
      <Checkbox
        checked={packed}
        disabled={!canEdit}
        aria-label={`${imperative}${targetName !== undefined ? ` for ${targetName}` : ""}`}
        onChange={() => onToggle?.(filterParticipant, targetIsUser)}
      />
    );
  }

  if (stagingEnabled) {
    const state = affectableRowPackingState(item, list, actor, true);
    return (
      <Checkbox
        checked={state === "packed"}
        state={state}
        disabled={!canEdit}
        aria-label={`${imperative} all for ${item.name}`}
        onChange={onAdvanceAll}
      />
    );
  }

  const participants = affectableParticipants(item, list, actor);
  const done = participants.filter((pid) => packingStateOf(item, pid, false) === "packed").length;
  const checked = participants.length > 0 && done === participants.length;
  const indeterminate = done > 0 && done < participants.length;

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={!canEdit}
      aria-label={`${imperative} all for ${item.name}`}
      onChange={onAdvanceAll}
    />
  );
};
